// Constants for selectors and classes
const SEARCH_SELECTORS = {
  searchModal: '[data-target="search"]',
  openButtons: '[data-open="search"]',
};

const SEARCH_CLASSES = {
  visible: "opacity-100",
  hidden: "opacity-0",
};

// Shared elements object
const SearchModalElements = {
  searchModal: null,
  searchInput: null,
  closeButtons: null,
  openButtons: null,
  searchProductsContainer: null,
  searchProductsWrapper: null,
  trendingCollectionsWrapper: null,
  searchCollectionWrapper: null,
  searchCollectionsContainer: null,
  featuredProductsWrapper: null,
  searchFormBackground: null,
};

let lastFocusedElement;
let debounceTimeout;

// URL params helper functions
function getUrlParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    searchOpen: params.get("search-open") === "true",
    searchQuery: params.get("search") || "",
  };
}

function updateUrlParams(searchOpen, searchQuery) {
  const url = new URL(window.location);

  if (searchOpen) {
    url.searchParams.set("search-open", "true");
  } else {
    url.searchParams.delete("search-open");
  }

  if (searchQuery && searchQuery.trim()) {
    url.searchParams.set("search", searchQuery.trim());
  } else {
    url.searchParams.delete("search");
  }

  window.history.replaceState({}, "", url);
}

// Format money helper
function formatMoney(price, currency, locale) {
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return formatter.format(price).replace(/\.$/, "");
}

// Function to open the modal
function openSearchModal(skipUrlUpdate = false) {
  const elements = SearchModalElements;

  if (!elements.searchModal) {
    console.error("Search modal: Modal element not found");
    return;
  }

  lastFocusedElement = document.activeElement;
  elements.searchModal.hidden = false;
  elements.searchModal.setAttribute("aria-hidden", "false");

  elements.searchModal.setAttribute("data-modal-open", "true");
  document.body.setAttribute("data-modal", "true");

  requestAnimationFrame(() => {
    elements.searchModal.classList.add(SEARCH_CLASSES.visible);
    elements.searchModal.classList.remove(SEARCH_CLASSES.hidden);
  });

  if (elements.searchInput) {
    elements.searchInput.focus();
  }

  if (!skipUrlUpdate) {
    updateUrlParams(
      true,
      elements.searchInput ? elements.searchInput.value : "",
    );
  }

  // Close mega menu if it's open (desktop)
  if (typeof window.closeMegaMenu === "function") {
    window.closeMegaMenu();
  }

  // Close mobile mega menu if it's open
  const mobileMegaMenu = document.querySelector(
    '[data-target="mega-menu-mobile"]',
  );
  if (mobileMegaMenu && !mobileMegaMenu.hidden) {
    if (typeof window.closeMobileNav === "function") {
      window.closeMobileNav();
    }
  }
}

// Function to close the modal
function closeSearchModal(skipUrlUpdate = false) {
  const elements = SearchModalElements;

  if (!elements.searchModal) return;

  elements.searchModal.setAttribute("data-modal-open", "false");

  if (document.querySelectorAll('[data-modal-open="true"]').length === 0) {
    document.body.setAttribute("data-modal", "false");
  }

  elements.searchModal.classList.add(SEARCH_CLASSES.hidden);
  elements.searchModal.classList.remove(SEARCH_CLASSES.visible);

  if (!skipUrlUpdate) {
    updateUrlParams(false, "");
  }

  setTimeout(() => {
    elements.searchModal.hidden = true;
    elements.searchModal.setAttribute("aria-hidden", "true");
    if (lastFocusedElement) lastFocusedElement.focus();
  }, 300);
}

// Expose functions globally
window.openSearchModal = openSearchModal;
window.closeSearchModal = closeSearchModal;

// Initialize DOM elements
function initSearchModal() {
  SearchModalElements.searchModal = document.querySelector(
    SEARCH_SELECTORS.searchModal,
  );
  SearchModalElements.openButtons = document.querySelectorAll(
    SEARCH_SELECTORS.openButtons,
  );

  if (!SearchModalElements.searchModal) {
    console.warn("Search modal: Modal element not found");
    return;
  }

  // Get all child elements
  SearchModalElements.closeButtons =
    SearchModalElements.searchModal.querySelectorAll(".full-modal__close");
  SearchModalElements.searchInput =
    SearchModalElements.searchModal.querySelector('input[name="q"]');
  SearchModalElements.searchProductsContainer =
    SearchModalElements.searchModal.querySelector(".search-products");
  SearchModalElements.searchProductsWrapper =
    SearchModalElements.searchModal.querySelector(".search-products-wrapper");
  SearchModalElements.trendingCollectionsWrapper =
    SearchModalElements.searchModal.querySelector(
      ".trending-collections-wrapper",
    );
  SearchModalElements.searchCollectionWrapper =
    SearchModalElements.searchModal.querySelector(
      ".search-collections-wrapper",
    );
  SearchModalElements.searchCollectionsContainer =
    SearchModalElements.searchModal.querySelector(".search-collections");
  SearchModalElements.featuredProductsWrapper =
    SearchModalElements.searchModal.querySelector(".featured-products-wrapper");
  SearchModalElements.searchFormBackground = document.querySelector(
    ".search-form-background",
  );

  // Initial setup - hide the modal
  SearchModalElements.searchModal.hidden = true;

  // Get currency and locale from data attributes
  const shopCurrency =
    SearchModalElements.searchModal.getAttribute("data-shop-currency") || "USD";
  const locale =
    SearchModalElements.searchModal.getAttribute("data-locale") || "en";

  // Event Listeners
  SearchModalElements.openButtons.forEach((button) => {
    button.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      openSearchModal(false);
    });
  });

  SearchModalElements.closeButtons.forEach((button) => {
    button.addEventListener("click", () => {
      closeSearchModal(false);
    });
  });

  // Close modal on ESC key
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !SearchModalElements.searchModal.hidden) {
      closeSearchModal(false);
    }
  });

  // Close modal when clicking outside
  if (SearchModalElements.searchFormBackground) {
    SearchModalElements.searchFormBackground.addEventListener(
      "click",
      (event) => {
        if (event.target !== SearchModalElements.searchInput) {
          closeSearchModal(false);
        }
      },
    );
  }

  // Trap focus within the modal
  SearchModalElements.searchModal.addEventListener("keydown", (event) => {
    if (event.key === "Tab") {
      const focusableElements =
        SearchModalElements.searchModal.querySelectorAll(
          "a[href], button, textarea, input, select",
        );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  });

  // Handle dynamic search
  if (SearchModalElements.searchInput) {
    SearchModalElements.searchInput.addEventListener("input", () => {
      const query = SearchModalElements.searchInput.value.trim();

      // Update URL params when search query changes (only if modal is open)
      if (!SearchModalElements.searchModal.hidden) {
        updateUrlParams(true, query);
      }

      // Debounce to prevent excessive requests
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        if (query.length < 1) {
          SearchModalElements.searchProductsContainer.innerHTML = "";
          SearchModalElements.searchCollectionsContainer.innerHTML = "";

          SearchModalElements.searchProductsWrapper.classList.add("hidden");
          if (SearchModalElements.featuredProductsWrapper) {
            SearchModalElements.featuredProductsWrapper.classList.remove(
              "hidden",
            );
          }

          if (SearchModalElements.trendingCollectionsWrapper) {
            SearchModalElements.trendingCollectionsWrapper.classList.remove(
              "hidden",
            );
            SearchModalElements.trendingCollectionsWrapper.classList.add(
              "flex",
            );
          }

          SearchModalElements.searchCollectionWrapper.classList.add("hidden");
          SearchModalElements.searchCollectionWrapper.classList.remove("flex");

          return;
        }

        fetch(
          `/search/suggest.json?q=${encodeURIComponent(
            query,
          )}&resources[type]=product,collection&resources[limit]=12&resources[options][fields]=title,product_type,variants.title,tag,body,variants.sku`,
        )
          .then((response) => response.json())
          .then((data) => {
            const products = data.resources.results.products;
            const collections = data.resources.results.collections;

            const filteredProducts = products.filter(
              (product) => !product.tags.includes("Hidden"),
            );

            if (SearchModalElements.searchProductsContainer) {
              if (filteredProducts.length > 0) {
                // Hide featured products immediately
                if (SearchModalElements.featuredProductsWrapper) {
                  SearchModalElements.featuredProductsWrapper.classList.add(
                    "hidden",
                  );
                }

                // Show wrapper but keep it invisible until cards load
                SearchModalElements.searchProductsWrapper.classList.remove(
                  "hidden",
                );
                SearchModalElements.searchProductsWrapper.classList.add(
                  "opacity-0",
                );

                // Smart approach: Use the search results page directly
                // This uses the existing main-search section which already renders card-product correctly
                const searchQuery =
                  SearchModalElements.searchInput.value.trim();
                const searchUrl = `/search?q=${encodeURIComponent(searchQuery)}&type=product&section_id=main-search`;

                fetch(searchUrl)
                  .then((response) => {
                    if (!response.ok) {
                      throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.text();
                  })
                  .then((html) => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, "text/html");

                    // Find the product grid from the search section
                    const productGrid = doc.querySelector("#product-grid");
                    if (!productGrid) {
                      throw new Error("Product grid not found");
                    }

                    // Get all product cards from the search results page
                    // The search results page already has the correct filtered products
                    const allCards = productGrid.querySelectorAll(".card");

                    // Show all products from search results (up to 12)
                    const matchingCards = Array.from(allCards).slice(0, 12);

                    if (matchingCards.length > 0) {
                      SearchModalElements.searchProductsContainer.innerHTML =
                        "";
                      matchingCards.forEach((card) => {
                        SearchModalElements.searchProductsContainer.appendChild(
                          card.cloneNode(true),
                        );
                      });
                      SearchModalElements.searchProductsContainer.style.gridTemplateColumns = `repeat(${matchingCards.length}, 14rem)`;

                      // Fade in the wrapper after cards are loaded
                      requestAnimationFrame(() => {
                        SearchModalElements.searchProductsWrapper.classList.remove(
                          "opacity-0",
                        );
                        SearchModalElements.searchProductsWrapper.classList.add(
                          "opacity-100",
                        );
                      });
                    } else {
                      throw new Error("No matching cards found");
                    }
                  })
                  .catch((error) => {
                    console.error("Error fetching search results:", error);
                    SearchModalElements.searchProductsContainer.innerHTML =
                      "<p>Error loading products.</p>";
                    // Still fade in even on error
                    requestAnimationFrame(() => {
                      SearchModalElements.searchProductsWrapper.classList.remove(
                        "opacity-0",
                      );
                      SearchModalElements.searchProductsWrapper.classList.add(
                        "opacity-100",
                      );
                    });
                  });
              } else {
                SearchModalElements.searchProductsContainer.innerHTML =
                  "<p>No products found.</p>";
              }
            }

            // Get collections from products (more relevant than search API collections)
            const getCollectionsFromProducts = async () => {
              const collectionMap = new Map();

              // If we have products, fetch their collections
              if (filteredProducts.length > 0) {
                // Fetch collections from each product
                const productCollectionPromises = filteredProducts
                  .slice(0, 5) // Limit to first 5 products to avoid too many requests
                  .map((product) => {
                    return fetch(`${product.url}.json`)
                      .then((response) => {
                        if (!response.ok) {
                          throw new Error(
                            `HTTP error! status: ${response.status}`,
                          );
                        }
                        return response.json();
                      })
                      .then((data) => {
                        if (data.product && data.product.collections) {
                          data.product.collections.forEach((collection) => {
                            if (!collectionMap.has(collection.handle)) {
                              collectionMap.set(collection.handle, {
                                title: collection.title,
                                url: `/collections/${collection.handle}`,
                                handle: collection.handle,
                                productCount: 0,
                              });
                            }
                            // Count how many products from our search are in this collection
                            collectionMap.get(collection.handle).productCount +=
                              1;
                          });
                        }
                      })
                      .catch((error) => {
                        console.error(
                          `Error fetching collections for product ${product.id}:`,
                          error,
                        );
                      });
                  });

                await Promise.all(productCollectionPromises);
              }

              // Combine with search API collections (add any we don't have)
              collections.forEach((collection) => {
                if (!collectionMap.has(collection.handle)) {
                  collectionMap.set(collection.handle, {
                    title: collection.title,
                    url: collection.url,
                    handle: collection.handle,
                    productCount: 0,
                  });
                }
              });

              // Convert to array and sort by product count (collections with more matching products first)
              const allCollections = Array.from(collectionMap.values())
                .sort((a, b) => b.productCount - a.productCount)
                .slice(0, 6); // Limit to 6 collections

              return allCollections;
            };

            // Display collections
            getCollectionsFromProducts()
              .then((allCollections) => {
                if (allCollections.length > 0) {
                  if (SearchModalElements.trendingCollectionsWrapper) {
                    SearchModalElements.trendingCollectionsWrapper.classList.add(
                      "hidden",
                    );
                    SearchModalElements.trendingCollectionsWrapper.classList.remove(
                      "flex",
                    );
                  }

                  SearchModalElements.searchCollectionWrapper.classList.remove(
                    "hidden",
                  );
                  SearchModalElements.searchCollectionWrapper.classList.add(
                    "flex",
                  );
                  // Start invisible for fade-in
                  SearchModalElements.searchCollectionWrapper.classList.add(
                    "opacity-0",
                  );

                  SearchModalElements.searchCollectionsContainer.innerHTML =
                    allCollections
                      .map(
                        (collection) => `
                      <a href="${collection.url}" class="text-lg leading-[1.2] transition-transform duration-100 hover:translate-x-1">
                        ${collection.title}
                      </a>
                  `,
                      )
                      .join("");

                  // Fade in collections after a brief delay
                  requestAnimationFrame(() => {
                    setTimeout(() => {
                      SearchModalElements.searchCollectionWrapper.classList.remove(
                        "opacity-0",
                      );
                      SearchModalElements.searchCollectionWrapper.classList.add(
                        "opacity-100",
                      );
                    }, 100);
                  });
                } else {
                  SearchModalElements.searchCollectionWrapper.classList.add(
                    "hidden",
                  );
                }
              })
              .catch((error) => {
                console.error("Error getting collections:", error);
                // Fallback to search API collections only
                if (collections.length > 0) {
                  SearchModalElements.searchCollectionWrapper.classList.remove(
                    "hidden",
                  );
                  SearchModalElements.searchCollectionWrapper.classList.add(
                    "flex",
                  );
                  SearchModalElements.searchCollectionsContainer.innerHTML =
                    collections
                      .map(
                        (collection) => `
                      <a href="${collection.url}" class="text-lg leading-[1.2] transition-transform duration-100 hover:translate-x-1">
                        ${collection.title}
                      </a>
                  `,
                      )
                      .join("");
                }
              });
          })
          .catch((error) => {
            console.error("Error fetching search results:", error);
            if (SearchModalElements.searchProductsContainer) {
              SearchModalElements.searchProductsContainer.innerHTML =
                "<p>Error fetching results.</p>";
            }
          });
      }, 200);
    });
  }

  // Initialize modal state from URL params
  const urlParams = getUrlParams();
  if (urlParams.searchQuery && SearchModalElements.searchInput) {
    SearchModalElements.searchInput.value = urlParams.searchQuery;
    SearchModalElements.searchInput.dispatchEvent(new Event("input"));
  }
  if (urlParams.searchOpen) {
    openSearchModal(true);
  }
}

// Initialize when DOM is loaded
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSearchModal);
} else {
  initSearchModal();
}
